import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import './Sidebar.css';

interface SidebarProps {
  onSelectOffer?: (offer: any) => void;
}

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactElement;
  badge?: number;
}

const Sidebar: React.FC<SidebarProps> = () => {
  const [unreadOffersCount, setUnreadOffersCount] = useState(0);
  const [manualReviewCount, setManualReviewCount] = useState(0);
  const [pendingLendersCount, setPendingLendersCount] = useState(0);
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Count pending lenders (admin only)
  useEffect(() => {
    if (userProfile?.role !== 'admin') return;
    const fetchPending = async () => {
      try {
        const fn = httpsCallable<unknown, { success: boolean; pendingLenders: unknown[] }>(functions, 'getPendingLenders');
        const result = await fn();
        if (result.data.success) {
          setPendingLendersCount(result.data.pendingLenders.length);
        }
      } catch {
        // Non-critical
      }
    };
    fetchPending();
  }, [userProfile]);

  // Count offers pending manual review (admin only)
  useEffect(() => {
    if (userProfile?.role !== 'admin') return;
    const q = query(
      collection(db, 'offers'),
      where('parsingStatus', '==', 'manual_review')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setManualReviewCount(snapshot.size);
    });
    return () => unsubscribe();
  }, [userProfile]);

  // Count unread offers (offers submitted in last 7 days)
  useEffect(() => {
    if (!currentUser) return;

    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const q = query(
      collection(db, 'offers'),
      where('listingUserId', '==', currentUser.uid),
      where('submissionDate', '>=', sevenDaysAgo)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setUnreadOffersCount(snapshot.size);
    }, (error) => {
      console.error('Error fetching unread offers:', error);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  const navItems: NavItem[] = [
    { 
      path: '/listings', 
      label: 'Listings', 
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
          <polyline points="9 22 9 12 15 12 15 22"></polyline>
        </svg>
      )
    },
    { 
      path: '/offers', 
      label: 'Offers', 
      badge: unreadOffersCount,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
        </svg>
      )
    },
    {
      path: '/my-submissions',
      label: 'Sent',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
          <polyline points="17 8 12 3 7 8"></polyline>
          <line x1="12" y1="3" x2="12" y2="15"></line>
        </svg>
      )
    },
    {
      path: '/lenders',
      label: 'Lenders',
      badge: userProfile?.role === 'admin' ? pendingLendersCount : undefined,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
      )
    },
    { 
      path: '/contact', 
      label: 'Contact', 
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
        </svg>
      )
    },
  ];

  // Admin-only navigation items
  const adminNavItems: NavItem[] = [
    {
      path: '/review-queue',
      label: 'Review Queue',
      badge: manualReviewCount,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
      )
    },
    {
      path: '/admin-settings',
      label: 'Admin Settings',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7"></rect>
          <rect x="14" y="3" width="7" height="7"></rect>
          <rect x="14" y="14" width="7" height="7"></rect>
          <rect x="3" y="14" width="7" height="7"></rect>
        </svg>
      )
    },
  ];

  // Combine nav items based on user role
  const isAdmin = userProfile?.role === 'admin';
  const allNavItems = isAdmin ? [...navItems, ...adminNavItems] : navItems;

  return (
    <aside className="app-sidebar">
      <div className="sidebar-brand" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
        <div className="brand-icon">★</div>
        <div className="brand-text">
          <div className="brand-name">Score My</div>
          <div className="brand-name">Offer</div>
        </div>
      </div>

      <nav className="sidebar-nav">
        {allNavItems.map((item) => (
          <button
            key={item.path}
            className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
            onClick={() => navigate(item.path)}
            title={item.label}
          >
            <div className="nav-icon-wrapper">
              {item.icon}
              {item.badge !== undefined && item.badge > 0 && (
                <span className="nav-badge">{item.badge}</span>
              )}
            </div>
            <span className="nav-label">{item.label}</span>
          </button>
        ))}
      </nav>
    </aside>
  );
};

export default Sidebar;
